import { MoreVert, ViewColumn } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import type { Sort, SortDirection } from "@store/utils/queryStateUtils";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

import { COPY_ICON_SPACING } from "@helpers/constants";
import { hasKey, normalizePathname } from "@helpers/utils";

import FieldDisplay from "../../data/FieldDisplay";
import type {
  ActionType,
  BaseDataShape,
  Columns,
  ExtendedDataShapeField,
  FieldRenderers,
  RowAction,
} from "../../data/types";
import DescriptiveTooltip from "../DescriptiveTooltip";
import ResourceStarButton, {
  RESOURCE_STAR_HOVER_STYLES,
} from "../ResourceStarButton/ResourceStarButton";
import { ColumnMenu } from "./ColumnMenu";
import { loadStoredColumnSizes, saveColumnSizes } from "./columnUtils";
import { useDataTableColumns } from "./useDataTableColumns";

/** Width of the leading star-toggle column, in px. See the `starColumn` prop. */
const STAR_COLUMN_SIZE = 32;

const TooltipWrapper = styled("div")({
  display: "inline-block",
});

const ActionsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  alignItems: "center",
  flexShrink: 0,
}));

const ActionIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isDisabled",
})<{ isDisabled?: boolean }>(({ theme, isDisabled }) => ({
  color: isDisabled ? theme.palette.text.disabled : undefined,
}));

const RowActionsContainer = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
});

const ColumnSelectionButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
}));

interface StyledHeaderCellProps {
  columnSize?: number;
  resizable?: boolean;
}

const StyledHeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "columnSize" && prop !== "resizable",
})<StyledHeaderCellProps>(({ theme, columnSize, resizable }) => ({
  position: "relative",
  width: columnSize ? `${columnSize}px` : "auto",
  minWidth: columnSize ? `${columnSize}px` : undefined,
  background: theme.palette.grey[200],
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  userSelect: resizable ? "none" : "auto",
}));

const StyledBodyCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== "columnSize" && prop !== "isFirstCell" && prop !== "isStarCell",
})<{
  columnSize?: number;
  isFirstCell?: boolean;
  isStarCell?: boolean;
}>(({ theme, columnSize, isFirstCell, isStarCell }) => ({
  width: columnSize && columnSize !== 150 ? `${columnSize}px` : "auto",
  minWidth: columnSize && columnSize !== 150 ? `${columnSize}px` : undefined,
  maxWidth: columnSize && columnSize !== 150 ? `${columnSize}px` : undefined,
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingRight: isStarCell ? theme.spacing(0.5) : COPY_ICON_SPACING,
  paddingLeft: isStarCell
    ? theme.spacing(0.5)
    : isFirstCell
      ? theme.spacing(2.5)
      : theme.spacing(1),
  textAlign: isStarCell ? "center" : undefined,
}));

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "2px",
    borderRadius: theme.shape.borderRadius,
    "& .MuiTableSortLabel-icon": {
      opacity: 1,
    },
  },
}));

const ResizeHandle = styled("div")(() => ({
  position: "absolute",
  right: 0,
  top: 0,
  height: "100%",
  width: "5px",
  cursor: "col-resize",
  zIndex: 1,
  userSelect: "none",
}));

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "hasRowClick" && prop !== "highlighted",
})<{ hasRowClick?: boolean; highlighted?: boolean }>(
  ({ theme, hasRowClick, highlighted }) => ({
    cursor: hasRowClick ? "pointer" : "default",
    backgroundColor: highlighted ? theme.palette.action.selected : undefined,
    "&:focus-visible": hasRowClick
      ? {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "-2px",
        }
      : {},
    ...RESOURCE_STAR_HOVER_STYLES,
  }),
);

const FieldWithActionsContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "actionsAlign",
})<{ actionsAlign?: "left" | "right" }>(({ theme, actionsAlign }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  ...(actionsAlign === "right" && {
    justifyContent: "space-between",
    width: "100%",
  }),
}));

const FieldContentWrapper = styled("div")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  position: "relative",
  paddingRight: theme.spacing(1.5),
}));

const TableFooter = styled(Paper)(({ theme }) => ({
  boxShadow: "none",
  borderTop: `1px solid ${theme.palette.divider}`,
  position: "sticky",
  bottom: 0,
  left: 0,
  right: 0,

  "& .MuiTablePagination-toolbar": {
    marginRight: "60px",
  },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  position: "relative",
  width: "100%",
  height: "100%",
  maxHeight: "calc(100vh - 200px)",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "white",
  overflow: "auto",
  "& .MuiTableHead-root": {
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

function getActionProps(
  action: ActionType,
  basename: string,
  handleCallback?: () => void,
) {
  const disabled = !!action.disabled;

  if (hasKey(action, "onClick") && action.onClick) {
    return {
      onClick: (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent row click from firing
        if (!disabled && action.onClick) {
          action.onClick();
        }
        if (handleCallback) handleCallback();
      },
      disabled,
    };
  }

  if (hasKey(action, "internalLink") && action.internalLink) {
    return {
      component: Link,
      to: `${basename}${action.internalLink}`,
      onClick: (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent row click from firing
        if (handleCallback) handleCallback();
      },
      disabled,
    };
  }

  if (hasKey(action, "externalLink") && action.externalLink) {
    return {
      component: "a" as const,
      href: action.externalLink,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick: (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent row click from firing
        if (handleCallback) handleCallback();
      },
      disabled,
    };
  }

  return { disabled };
}

function ContextActions<DataShape extends BaseDataShape>({
  rowData,
  actions,
}: {
  rowData: DataShape;
  actions: RowAction<DataShape>[];
}) {
  const visibleActions = actions.filter(
    (action) => !action.hidden || !action.hidden(rowData),
  );

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <ActionsContainer>
      {visibleActions.map((action, index) => {
        const actionResult = action.getAction(rowData);
        if (!actionResult) return null;

        const isDisabled = actionResult.disabled || false;
        const tooltipText = isDisabled
          ? actionResult.disabledTooltip
          : action.label;

        const iconButton = (
          <ActionIconButton
            key={index}
            size="small"
            isDisabled={isDisabled}
            aria-label={action.label}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation(); // Prevent row click from firing
              if (actionResult.onClick) {
                actionResult.onClick();
              } else if (actionResult.internalLink) {
                window.location.href = actionResult.internalLink;
              } else if (actionResult.externalLink) {
                window.open(actionResult.externalLink, "_blank");
              }
            }}
          >
            {action.icon ? action.icon(isDisabled) : null}
          </ActionIconButton>
        );

        return tooltipText ? (
          <DescriptiveTooltip key={index} title={tooltipText}>
            <TooltipWrapper>{iconButton}</TooltipWrapper>
          </DescriptiveTooltip>
        ) : (
          iconButton
        );
      })}
    </ActionsContainer>
  );
}

function RowActionsMenu<DataShape extends BaseDataShape>({
  rowData,
  actions,
}: {
  rowData: DataShape;
  actions: RowAction<DataShape>[];
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const location = useLocation();
  const basename = normalizePathname(location.pathname);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent row click from firing
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const visibleActions = actions.filter(
    (action) => !action.hidden || !action.hidden(rowData),
  );

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <RowActionsContainer>
      <IconButton
        size="small"
        onClick={handleClick}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label="row actions"
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {visibleActions.map((action) => {
          const actionResult = action.getAction(rowData);
          if (!actionResult) return null;

          const actionProps = getActionProps(
            actionResult,
            basename,
            handleClose,
          );

          return (
            <MenuItem key={action.label} {...actionProps}>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={action.label}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </RowActionsContainer>
  );
}

interface DataTableProps<
  QueryableDataShape extends BaseDataShape,
  DataShape extends QueryableDataShape,
  DataField extends ExtendedDataShapeField<DataShape>,
> {
  data: DataShape[];
  columns: Columns<DataShape, DataField>;
  fieldRenderers?: FieldRenderers<DataField["dataType"]>;
  sort: Sort<QueryableDataShape>;
  paging: {
    offset: number;
    limit: number;
    total?: { count: number; isCountFinal: boolean };
  };
  setSort: (sort: Sort<QueryableDataShape>) => void;
  onPrevPageClick: () => void;
  onNextPageClick: () => void;
  /**
   * Called on left-click, Ctrl/Cmd+click, middle-click, Enter, and Ctrl/Cmd+Enter.
   * The event carries ctrlKey/metaKey/button so the handler can open a new tab on
   * modifier gestures. Always use `useRowNavigation` — do not implement this logic inline.
   */
  onRowClick?: (
    row: DataShape,
    event?: React.MouseEvent | React.KeyboardEvent,
  ) => void;
  /**
   * Id of the row to render as highlighted, e.g. the row whose detail drawer is
   * open. Independent of `selectableRows` checkbox state.
   */
  highlightedRowId?: string;
  actions?: RowAction<DataShape>[];
  /**
   * When true, the trailing actions column renders the actions inline (icon +
   * tooltip) instead of behind a "⋮" overflow menu. Best for a single,
   * frequently-used row action such as delete.
   */
  inlineActions?: boolean;
  /** Adds a leading hover-reveal star toggle column, e.g. for bookmarking rows. */
  starColumn?: {
    isStarred: (row: DataShape) => boolean;
    onToggle: (row: DataShape) => void;
  };
  selectableRows?: boolean;
  // TODO: pageSizeOptions currently non-functional - displays dropdown but can't change size.
  // To fix: Add onPageSizeChange prop, wire to TablePagination's onRowsPerPageChange handler.
  // pageSizeOptions: number[];
  enableColumnSelection?: boolean;
  tableId?: string;
  renderColumnsButtonExternally?: boolean;
  onRenderColumnsButton?: (button: React.ReactNode) => void;
  /** Optional default sort to use when sorting is cleared. If not provided, the current sort will be kept. */
  defaultSort?: Sort<QueryableDataShape>;
  /** Accessible label for the table element, e.g. "Jobs table", "Access requests table". */
  ariaLabel: string;
  onCellCopied?: (fieldName: string) => void;
}

type SortFieldItem = {
  id: string;
  desc: boolean;
};

const updateSortFields = (
  currentFields: SortFieldItem[],
  newField: SortFieldItem,
): SortFieldItem[] => {
  const existingIndex = currentFields.findIndex(
    (field) => field.id === newField.id,
  );
  if (existingIndex >= 0) {
    // Toggle direction of existing column
    const result = [...currentFields];
    result[existingIndex] = {
      id: newField.id,
      desc: !result[existingIndex].desc,
    };
    return result;
  } else {
    // Add new column to sort
    return [...currentFields, newField];
  }
};

const sortFieldsToStrings = (
  sortFields: SortFieldItem[],
): { fieldNameList: string; directionList: string } => {
  const fieldNameList = sortFields.map((field) => field.id).join(",");
  const directionList = sortFields
    .map((field) => (field.desc ? "desc" : "asc"))
    .join(",");
  return { fieldNameList, directionList };
};

const SortPriorityIndicator = ({ index }: { index: number }) => (
  <Box
    component="span"
    sx={{
      position: "relative",
      top: "-8px",
      right: "-2px",
      fontSize: "10px",
      fontWeight: "bold",
      color: "primary.main",
    }}
  >
    {index + 1}
  </Box>
);

function DataTable<
  QueryableDataShape extends BaseDataShape,
  DataShape extends QueryableDataShape,
  DataField extends ExtendedDataShapeField<DataShape>,
>({
  data,
  columns,
  fieldRenderers,
  sort,
  paging: { offset, limit, total },
  setSort,
  onPrevPageClick,
  onNextPageClick,
  onRowClick,
  highlightedRowId,
  actions,
  inlineActions = false,
  selectableRows,
  // pageSizeOptions,
  enableColumnSelection = false,
  tableId,
  renderColumnsButtonExternally = false,
  onRenderColumnsButton,
  defaultSort,
  ariaLabel,
  onCellCopied,
  starColumn,
}: DataTableProps<QueryableDataShape, DataShape, DataField>) {
  const [selected, setSelected] = useState<string[]>([]);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const {
    visibleColumns,
    hiddenColumns,
    handleColumnToggle,
    handleColumnMove,
    setDefaultColumns,
    isCurrentSelectionDefault,
  } = useDataTableColumns({
    tableId,
    enableColumnSelection,
    allColumns: columns,
  });

  const handleColumnMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setColumnMenuAnchor(event.currentTarget);
    },
    [],
  );

  const handleColumnMenuClose = useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleSaveAsDefault = useCallback(() => {
    setDefaultColumns(visibleColumns);
  }, [setDefaultColumns, visibleColumns]);

  const handleHeaderClick = useCallback(
    (column: any, e: React.MouseEvent) => {
      if (!column.getCanSort()) return;

      const multiSort = e.shiftKey || false;

      let desc = false;
      if (column.getIsSorted() === false) {
        desc = false;
      } else if (column.getIsSorted() === "asc") {
        desc = true;
      } else {
        desc = false;
      }

      const newSort = {
        id: column.id,
        desc: desc,
      };

      let currentSortFields: Array<SortFieldItem> = [];
      if (sort.fieldName) {
        const fieldNames = sort.fieldName.split(",");
        const directions = sort.direction.split(",");

        currentSortFields = fieldNames.map((field, index) => ({
          id: field,
          desc: directions[index] === "desc",
        }));
      }

      if (multiSort && currentSortFields.length > 0) {
        const updatedSortFields = updateSortFields(currentSortFields, newSort);
        const { fieldNameList, directionList } =
          sortFieldsToStrings(updatedSortFields);

        setSort({
          fieldName: fieldNameList as string & keyof QueryableDataShape,
          direction: directionList as SortDirection,
        });
      } else {
        setSort({
          fieldName: newSort.id as string & keyof QueryableDataShape,
          direction: newSort.desc ? "desc" : "asc",
        });
      }
    },
    [sort.fieldName, sort.direction, setSort],
  );

  const handleSortingChange = useCallback(
    (newSorting: any, event?: React.MouseEvent) => {
      if (newSorting.length > 0) {
        const newSortState = newSorting[0];

        // Use our header click handler with the event
        if (event) {
          handleHeaderClick(
            {
              id: newSortState.id,
              getIsSorted: () => false,
              getCanSort: () => true,
            },
            event,
          );
        } else {
          // No event means just use single column sort
          setSort({
            fieldName: newSortState.id as string & keyof QueryableDataShape,
            direction: newSortState.desc ? "desc" : "asc",
          });
        }
      } else if (defaultSort) {
        // Handle the case when sorting is cleared - use provided default sort
        setSort({
          fieldName: defaultSort.fieldName,
          direction: defaultSort.direction,
        });
      }
      // If no defaultSort is provided and sorting is cleared, keep current sort
    },
    [handleHeaderClick, setSort, defaultSort],
  );

  useEffect(() => {
    setSelected([]);
  }, [data]);

  const handleRowSelectionChange = useCallback(
    (updater: any) => {
      const currentSelection: RowSelectionState = {};
      selected.forEach((id) => {
        const index = data.findIndex((row) => row.id === id);
        if (index !== -1) {
          currentSelection[index] = true;
        }
      });

      const newSelection =
        typeof updater === "function" ? updater(currentSelection) : updater;
      const selectedIds = Object.keys(newSelection)
        .filter((key) => newSelection[key])
        .map((index) => data[parseInt(index)]?.id)
        .filter(Boolean);
      setSelected(selectedIds);
    },
    [data, selected],
  );

  const [columnSizing, setColumnSizing] = useState(() =>
    loadStoredColumnSizes(tableId || ""),
  );

  // Stable cell renderer for the actions column.  TanStack Table's
  // flexRender() wraps function renderers via React.createElement(), so a
  // new function reference causes React to unmount/remount the entire
  // subtree — destroying state like open menus inside RowActionsMenu.
  // We keep the reference stable and read the latest actions from a ref.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const renderActionsCell = useCallback(
    ({ row }: { row: { original: DataShape } }) =>
      inlineActions ? (
        <RowActionsContainer>
          <ContextActions
            rowData={row.original}
            actions={actionsRef.current!}
          />
        </RowActionsContainer>
      ) : (
        <RowActionsMenu rowData={row.original} actions={actionsRef.current!} />
      ),
    [inlineActions],
  );

  // Convert columns to TanStack Table format
  const tableColumns = useMemo<ColumnDef<DataShape>[]>(() => {
    const cols: ColumnDef<DataShape>[] = [];

    if (starColumn) {
      cols.push({
        id: "star",
        header: "",
        cell: ({ row }) => (
          <ResourceStarButton
            starred={starColumn.isStarred(row.original)}
            onToggle={() => starColumn.onToggle(row.original)}
          />
        ),
        size: STAR_COLUMN_SIZE,
        minSize: STAR_COLUMN_SIZE,
        maxSize: STAR_COLUMN_SIZE,
        enableSorting: false,
        enableResizing: false,
      });
    }

    if (selectableRows) {
      cols.push({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 40,
        minSize: 40,
        maxSize: 40,
        enableSorting: false,
        enableResizing: false,
      });
    }

    // When column selection is enabled, respect the order from visibleColumns
    const columnsToShow = enableColumnSelection
      ? visibleColumns
          .map((fieldName) =>
            columns.find((col) => col.fieldName === fieldName),
          )
          .filter((col): col is (typeof columns)[0] => col !== undefined)
      : columns;

    columnsToShow.forEach((col) => {
      cols.push({
        id: col.fieldName as string,
        accessorKey: col.fieldName as string,
        header: () => col.customHeader || col.label,
        meta: {
          hasCustomHeader: !!col.customHeader,
        },
        size: col.width
          ? typeof col.width === "number"
            ? col.width
            : parseInt(col.width.toString())
          : undefined,
        minSize: 50,
        maxSize: 600,
        enableResizing: true,
        cell: ({ getValue, row }) => {
          try {
            const value = getValue();
            const rowData = row.original;
            const hasContextActions =
              (col as any).contextActions &&
              (col as any).contextActions.length > 0;

            if (hasContextActions) {
              const align = (col as any).contextActionsAlign;
              const fieldDisplay = (
                <FieldDisplay
                  fieldValue={value}
                  dataType={col.dataType}
                  fieldRenderers={fieldRenderers}
                  onCopySuccess={
                    onCellCopied ? () => onCellCopied(col.fieldName) : undefined
                  }
                />
              );
              return (
                <FieldWithActionsContainer actionsAlign={align}>
                  {align === "right" ? (
                    <FieldContentWrapper>{fieldDisplay}</FieldContentWrapper>
                  ) : (
                    fieldDisplay
                  )}
                  <ContextActions
                    rowData={rowData}
                    actions={(col as any).contextActions}
                  />
                </FieldWithActionsContainer>
              );
            }

            return (
              <FieldDisplay
                fieldValue={value}
                dataType={col.dataType}
                fieldRenderers={fieldRenderers}
                onCopySuccess={
                  onCellCopied ? () => onCellCopied(col.fieldName) : undefined
                }
              />
            );
          } catch (error) {
            return "";
          }
        },
        enableSorting: col.isSortable,
      });
    });

    if (actions && actions.length > 0) {
      cols.push({
        id: "actions",
        header: "",
        cell: renderActionsCell,
        size: 50,
        minSize: 50,
        maxSize: 50,
        enableSorting: false,
        enableResizing: false,
      });
    }

    return cols;
  }, [
    columns,
    fieldRenderers,
    selectableRows,
    actions,
    renderActionsCell,
    enableColumnSelection,
    visibleColumns,
    onCellCopied,
    starColumn,
  ]);

  const sorting = useMemo(() => {
    if (!sort.fieldName) return [];
    return [
      {
        id: sort.fieldName as string,
        desc: sort.direction === "desc",
      },
    ];
  }, [sort.fieldName, sort.direction]);

  const rowSelection = useMemo(() => {
    const selection: RowSelectionState = {};
    selected.forEach((id) => {
      const index = data.findIndex((row) => row.id === id);
      if (index !== -1) {
        selection[index] = true;
      }
    });
    return selection;
  }, [selected, data]);

  const sortFields = useMemo<SortFieldItem[]>(() => {
    if (!sort.fieldName) return [];

    const fieldNames = sort.fieldName.includes(",")
      ? sort.fieldName.split(",")
      : [sort.fieldName];

    const directions = sort.direction.includes(",")
      ? sort.direction.split(",")
      : [sort.direction];

    return fieldNames.map((field, index) => ({
      id: field,
      desc: directions[index] === "desc",
    }));
  }, [sort.fieldName, sort.direction]);

  const handleColumnSizingChange = useCallback(
    (updater: any) => {
      const newSizing =
        typeof updater === "function" ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
      if (tableId) {
        saveColumnSizes(tableId, newSizing);
      }
    },
    [columnSizing, tableId],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true, // Disable client-side sorting, use server-side only
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      sorting,
      rowSelection,
      columnSizing,
    },
    onSortingChange: handleSortingChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnSizingChange: handleColumnSizingChange,
    enableRowSelection: selectableRows,
    enableMultiRowSelection: selectableRows,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const currentPage = Math.ceil(offset / limit);

  const columnsButton = useMemo(() => {
    return enableColumnSelection ? (
      <Button
        startIcon={<ViewColumn />}
        onClick={handleColumnMenuOpen}
        variant="outlined"
        size="small"
      >
        Columns ({visibleColumns.length}/{columns.length})
      </Button>
    ) : null;
  }, [
    enableColumnSelection,
    handleColumnMenuOpen,
    visibleColumns.length,
    columns.length,
  ]);
  // This is how we pass the column button to the parent component,
  // We do this because in some cases it works better for the parent component to render the button itself
  React.useEffect(() => {
    if (
      renderColumnsButtonExternally &&
      onRenderColumnsButton &&
      columnsButton
    ) {
      onRenderColumnsButton(columnsButton);
    }
  }, [renderColumnsButtonExternally, onRenderColumnsButton, columnsButton]);

  return (
    <>
      {enableColumnSelection && !renderColumnsButtonExternally && (
        <ColumnSelectionButtonContainer>
          {columnsButton}
        </ColumnSelectionButtonContainer>
      )}
      {enableColumnSelection && (
        <Popover
          open={Boolean(columnMenuAnchor)}
          anchorEl={columnMenuAnchor}
          onClose={handleColumnMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <ColumnMenu
            columns={columns}
            visibleColumns={visibleColumns}
            hiddenColumns={hiddenColumns}
            handleColumnToggle={handleColumnToggle}
            handleColumnMove={handleColumnMove}
            tableId={tableId}
            handleSaveAsDefault={handleSaveAsDefault}
            isCurrentSelectionDefault={isCurrentSelectionDefault}
            onClose={handleColumnMenuClose}
          />
        </Popover>
      )}
      <StyledTableContainer>
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          <Table stickyHeader size="small" aria-label={ariaLabel}>
            {starColumn && (
              // MUI's `stickyHeader` renders the table with `table-layout: fixed`,
              // so the browser proportionally stretches every column to fill the
              // table width even when a cell declares an explicit pixel size. A
              // <col> pins a column to its exact width regardless of that
              // stretching. The star column is always first, so a single <col>
              // here pins only it — every other column is left without a <col>
              // and keeps its previous (unpinned, proportionally-stretched)
              // sizing behavior untouched.
              <colgroup>
                <col style={{ width: `${STAR_COLUMN_SIZE}px` }} />
              </colgroup>
            )}
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const hasCustomHeader = (
                      header.column.columnDef.meta as any
                    )?.hasCustomHeader;
                    const isMultiSorted =
                      sortFields.length > 1 &&
                      sortFields.some((field) => field.id === header.column.id);
                    const renderedHeader = flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    );
                    const sortLabel = (
                      <StyledTableSortLabel
                        onClick={(e) => handleHeaderClick(header.column, e)}
                        active={
                          header.column.getIsSorted() !== false || isMultiSorted
                        }
                        direction={
                          isMultiSorted
                            ? sortFields.find(
                                (field) => field.id === header.column.id,
                              )?.desc
                              ? "desc"
                              : "asc"
                            : header.column.getIsSorted() === "desc"
                              ? "desc"
                              : "asc"
                        }
                      >
                        {renderedHeader}
                        {isMultiSorted && (
                          <SortPriorityIndicator
                            index={sortFields.findIndex(
                              (field) => field.id === header.column.id,
                            )}
                          />
                        )}
                      </StyledTableSortLabel>
                    );

                    let headerContent;
                    if (header.column.getCanSort()) {
                      // Columns with a custom header manage their own tooltip,
                      // so skip the "Click to sort" wrapper to avoid two
                      // tooltips overlapping. Sorting still works via the sort
                      // label's onClick.
                      headerContent = hasCustomHeader ? (
                        sortLabel
                      ) : (
                        <DescriptiveTooltip title="Click to sort. Hold Shift and click to sort by multiple columns">
                          {sortLabel}
                        </DescriptiveTooltip>
                      );
                    } else if (hasCustomHeader) {
                      // Custom headers implement their own tooltip; adding one
                      // here may mess with the custom header.
                      headerContent = <div>{renderedHeader}</div>;
                    } else {
                      headerContent = (
                        <DescriptiveTooltip title="This column is not sortable">
                          <span>{renderedHeader}</span>
                        </DescriptiveTooltip>
                      );
                    }

                    return (
                      <StyledHeaderCell
                        key={header.id}
                        columnSize={header.getSize()}
                        resizable={header.column.getCanResize()}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : ""
                            }
                          >
                            {headerContent}
                          </div>
                        )}
                        {header.column.getCanResize() && (
                          <ResizeHandle
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                          />
                        )}
                      </StyledHeaderCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <StyledTableRow
                  key={row.id}
                  selected={row.getIsSelected()}
                  highlighted={row.original.id === highlightedRowId}
                  onClick={(event) => onRowClick?.(row.original, event)}
                  onAuxClick={(event) => {
                    // Middle mouse button (button 1) does not fire onClick;
                    // forward it so useRowNavigation can open a new tab.
                    if (event.button === 1) {
                      onRowClick?.(row.original, event);
                    }
                  }}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onRowClick(row.original, event);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  hasRowClick={Boolean(onRowClick)}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <StyledBodyCell
                      key={cell.id}
                      columnSize={cell.column.getSize()}
                      isFirstCell={index === 0}
                      isStarCell={cell.column.id === "star"}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </StyledBodyCell>
                  ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TableFooter>
          <TablePagination
            component="div"
            rowsPerPageOptions={[limit]}
            count={total?.count ?? offset + data.length}
            rowsPerPage={limit}
            onPageChange={(evt, page) =>
              page > currentPage ? onNextPageClick() : onPrevPageClick()
            }
            page={currentPage}
            labelDisplayedRows={() => (
              <span>
                {offset + 1}-{offset + data.length} of{" "}
                {total ? (
                  `${total.count}${total.isCountFinal ? "" : "+"}`
                ) : (
                  <CircularProgress color="inherit" size={12} />
                )}
              </span>
            )}
          />
        </TableFooter>
      </StyledTableContainer>
    </>
  );
}

export default DataTable;
